import { useMemo } from 'react';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import { EntityWithTicketsQuery, Feature } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContextDataSelector } from '../CatalogDataContext';
import { useFilterQueryContext } from '../FilterQueryContext';
import {
  CatalogMetricsContextProviderProps,
  catalogMetricsEmptyContextWithSelector,
  createCatalogMetricsEmptyContext,
  ICatalogMetricsContext,
} from './CatalogMetrics.type';
import { getEventsToGetMetricsFor } from './CatalogMetrics.utils';

function CatalogMetricsEmptyContextProvider<
  TMetrics extends { currency: string | null },
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  entityType,
  children,
}: CatalogMetricsContextProviderProps<TMetrics, TQuery>) {
  const CatalogMetricsContext = createCatalogMetricsEmptyContext<TMetrics>();
  const CatalotMetricsContextWithSelector =
    catalogMetricsEmptyContextWithSelector<TMetrics>();

  const { loginContext } = useAppContext();
  const data = useCatalogDataContextDataSelector();

  const { filterQuery } = useFilterQueryContext<TQuery>();

  const groupsToGetMetricsFor = useMemo(() => {
    if (!data?.events) {
      return [];
    }
    return getEventsToGetMetricsFor(
      Object.values(data.events),
      entityType,
      filterQuery.sortBy,
      filterQuery.isSortDescending
    );
  }, [
    data?.events,
    entityType,
    filterQuery.sortBy,
    filterQuery.isSortDescending,
  ]);

  // Create empty metrics for all events
  const createEmptyMetrics = useMemo(() => {
    const emptyEventMetrics: Record<string, TMetrics> = {};
    const emptyEventDetailedMetrics: Record<string, TMetrics> = {};
    const emptyPerformerMetrics: Record<string, TMetrics> = {};
    const emptyVenueMetrics: Record<string, TMetrics> = {};

    groupsToGetMetricsFor.flat().forEach((event) => {
      // Create empty metrics object with all zero values
      const emptyMetric = {
        currency: loginContext?.user?.activeAccount?.currencyCode || null,
        // Add any other properties that TMetrics might have
      } as TMetrics;

      emptyEventMetrics[event.viagVirtId] = emptyMetric;
      emptyEventDetailedMetrics[event.viagVirtId] = emptyMetric;

      if (event.performerId) {
        emptyPerformerMetrics[event.performerId.toString()] = emptyMetric;
      }

      emptyVenueMetrics[event.venueId.toString()] = emptyMetric;
    });

    return {
      emptyEventMetrics,
      emptyEventDetailedMetrics,
      emptyPerformerMetrics,
      emptyVenueMetrics,
    };
  }, [groupsToGetMetricsFor, loginContext?.user?.activeAccount?.currencyCode]);

  const contextValues = useMemo<ICatalogMetricsContext<TMetrics>>(() => {
    return {
      isLoading: false, // Never loading since we don't fetch data
      eventDetailedMetrics: createEmptyMetrics.emptyEventDetailedMetrics,
      eventMetrics: createEmptyMetrics.emptyEventMetrics,
      performerMetrics: createEmptyMetrics.emptyPerformerMetrics,
      venueMetrics: createEmptyMetrics.emptyVenueMetrics,
      refreshMetrics: () => {
        // No-op since we don't fetch any data
      },
    };
  }, [createEmptyMetrics]);

  return (
    <CatalotMetricsContextWithSelector.Provider value={contextValues}>
      {children}
    </CatalotMetricsContextWithSelector.Provider>
  );
}

export { CatalogMetricsEmptyContextProvider };
